import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import api from '@/api';
import type { components } from '@/api/qraft/schema';
import { AlertBox } from '@/common/AlertBox';
import { InfoSectionContainer } from '@/common/InfoSection';
import { TabsBar } from '@/common/TabsBar';
import { WBSButton } from '@/common/WBSButton';
import { ConnectChannelModal } from '@/settings/ConnectChannelModal';
import { FormTitle, PageContent, PageHeader } from '@/settings/settingsStyles';
import { getMemberSettingsTabs } from '@/settings/tabData';
import { useCurrentUser } from '~/hooks/useCurrentUser';
import { ConnectionCard } from '~/modules/settings/ConnectionCard';
import { NewsletterSection } from '~/modules/settings/NewsletterSection';
import { NotificationCategory } from '~/modules/settings/NotificationCategory';
import { Title } from '~/styles/layoutElements';

type ContactChannel = components['schemas']['ContactChannel'];

/** Display names for the two connectable channels. */
const CHANNEL_LABELS: Record<ContactChannel, string> = {
  // Translation Needed
  sms: 'SMS Notifications',
  // Translation Needed
  whatsapp: 'WhatsApp Notifications',
};

export const Route = createFileRoute(
  '/_app/_users/members/$memberId/settings/notifications',
)({
  component: NotificationsRoute,
});

function NotificationsRoute() {
  const { t } = useTranslation();
  const { memberId } = Route.useParams();
  // Local edits, layered over the server's values until Save. Only the rows the
  // user touched are sent, so a stale read cannot overwrite a neighbouring
  // toggle they never saw.
  const [edits, setEdits] = useState<Record<string, Record<string, boolean>>>(
    {},
  );
  // The newsletter's edit, layered over `me.newsletter_subscribed` the same way:
  // `null` means untouched, so Save sends nothing.
  const [newsletterEdit, setNewsletterEdit] = useState<boolean | null>(null);
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);
  const [connecting, setConnecting] = useState<ContactChannel | null>(null);

  const { me } = useCurrentUser();
  const { data: preferences } = api.getNotificationPreferences.useQuery({});
  const { data: contactChannels } = api.getContactChannels.useQuery({});
  const { mutateAsync: updatePreferences, isPending: isSavingPreferences } =
    api.updateNotificationPreferences.useMutation();
  const {
    mutateAsync: setNewsletterSubscription,
    isPending: isSavingNewsletter,
  } = api.setMyNewsletterSubscription.useMutation();
  const { mutateAsync: disconnectChannel } =
    api.disconnectContactChannel.useMutation();
  // Save writes either half, so its spinner covers either mutation; `isLoading`
  // also disables the button.
  const isSaving = isSavingPreferences || isSavingNewsletter;

  // The settings layout renders nothing until `me` resolves, so this can't
  // happen at runtime; it narrows the type. An early return rather than a `??`
  // default, because either default would show the wrong newsletter state.
  if (me === undefined) return null;

  const handleToggle = (itemName: string, channel: string): void => {
    const current = preferences?.categories
      .flatMap((category) => category.items)
      .find((item) => item.name === itemName);
    const serverValue = current?.settings[channel];
    if (serverValue === undefined) return;

    setEdits((prev) => ({
      ...prev,
      [itemName]: {
        ...prev[itemName],
        [channel]: !(prev[itemName]?.[channel] ?? serverValue),
      },
    }));
  };

  const handleSaveChanges = async (): Promise<void> => {
    if (Object.keys(edits).length > 0) {
      await updatePreferences({ body: { settings: edits } });
      await api.getNotificationPreferences.invalidateQueries();
      setEdits({});
    }
    // The newsletter is applied by Save, not on click. Legacy's control writes
    // as soon as its link is followed; an immediate write beside a batched form
    // would mix two idioms on one page. The cost is two mutations when both
    // halves change.
    if (
      newsletterEdit !== null &&
      newsletterEdit !== me.newsletter_subscribed
    ) {
      await setNewsletterSubscription({ body: { subscribed: newsletterEdit } });
      await api.getCurrentUser.invalidateQueries();
      setNewsletterEdit(null);
    }
    setShowSuccessAlert(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Connecting or disconnecting adds or removes a column on the grid below, so
  // both queries have to refresh together.
  const refreshChannels = async (): Promise<void> => {
    await Promise.all([
      api.getContactChannels.invalidateQueries(),
      api.getNotificationPreferences.invalidateQueries(),
    ]);
  };

  const handleDisconnect = async (channel: ContactChannel): Promise<void> => {
    await disconnectChannel({ path: { channel } });
    await refreshChannels();
  };

  return (
    <PageContent>
      {showSuccessAlert && (
        <AlertBox variant="success">{t('profile.changes-saved')}</AlertBox>
      )}
      <PageHeader>
        <Title>{t('wbs.account-settings')}</Title>
      </PageHeader>

      <div style={{ marginBottom: 11 }}>
        <TabsBar
          tabs={getMemberSettingsTabs(t)}
          base={`/members/${memberId}/settings`}
          selected="/notifications"
        />
      </div>
      <InfoSectionContainer>
        <FormTitle>{t('notifications.notification-preferences')}</FormTitle>
        <Subtitle>{t('notifications.choose-your-email-and')}</Subtitle>

        <ConnectionSection>
          {(contactChannels?.channels ?? []).map((status) => (
            <ConnectionCard
              key={status.channel}
              title={
                status.connected
                  ? // Translation Needed
                    `${CHANNEL_LABELS[status.channel]}: ${status.number}`
                  : // Translation Needed
                    `Connect to ${CHANNEL_LABELS[status.channel]}`
              }
              connected={status.connected}
              onConnect={() => setConnecting(status.channel)}
              onDisconnect={() => handleDisconnect(status.channel)}
            />
          ))}
          {/* Push notifications are connected by installing the app, so this
              card has no action on either stack — legacy's FullApp type shows
              store links instead of a Connect button. */}
          <ConnectionCard
            // Translations Needed
            title="App Notifications is connected"
            subtitle="You can disconnect push notification from within WBS FullApp"
            connected
          />
        </ConnectionSection>

        <CategoriesWrapper>
          {(preferences?.categories ?? []).map((category) => (
            <NotificationCategory
              key={category.key}
              title={category.label}
              channels={preferences?.channels ?? []}
              items={category.items}
              preferences={edits}
              onToggle={handleToggle}
            />
          ))}
          {/* Inside the wrapper and last, so `CategoriesWrapper`'s
              `:last-child` rule strips this section's bottom border instead of
              the final category's: the newsletter keeps a divider above it and
              sits flush against Save. */}
          <NewsletterSection
            title={t('wbs.newsletter-preferences')}
            label={t('wbs.subscribe-to-our-newsletter')}
            checked={newsletterEdit ?? me.newsletter_subscribed}
            onToggle={() =>
              setNewsletterEdit(!(newsletterEdit ?? me.newsletter_subscribed))
            }
          />
        </CategoriesWrapper>

        <WBSButton
          wide
          onClick={handleSaveChanges}
          isLoading={isSaving}
          style={{ margin: '2px 0 19px 0' }}
        >
          {t('actions.save-changes')}
        </WBSButton>
      </InfoSectionContainer>

      <ConnectChannelModal
        channel={connecting}
        label={connecting ? CHANNEL_LABELS[connecting] : ''}
        onClose={() => setConnecting(null)}
        onConnected={refreshChannels}
      />
    </PageContent>
  );
}

const Subtitle = styled.p`
  color: var(--grey-500);
  font-size: 16px;
  letter-spacing: -0.27px;
  margin: 0 0 31px 0;
`;

const ConnectionSection = styled.div`
  border-bottom: 1px solid var(--grey-150);
  display: grid;
  gap: 16px;
  grid-template-columns: repeat(3, 1fr);
  margin-bottom: 16px;
  padding-bottom: 31px;
`;

const CategoriesWrapper = styled.div`
  & > *:last-child {
    border-bottom: none;
  }
`;
